/**
 * Sticky header scroll states + corner cut-out (`sections/header.liquid`).
 *
 * The sticky element is the section wrapper, not the <header> inside it (see the
 * section's own stylesheet for why); this toggles the classes that compact it
 * and animate the corner cut-out.
 *
 * States (matching the reference's `data-sticky-type="always"` header exactly):
 * - Default: header at full height, top corners rounded, bottom corners hidden (pushed out)
 * - .header-scrolled: pinned past one header-height (10px hysteresis both ways),
 *   compacted to 75% padding, bottom corners visible (slide in + stroke)
 * - .header-section--hidden: scrolled past section bottom + 100px AND scrolling down.
 *   The class is toggled at every width, but its CSS only moves the header on
 *   phones — the reference ships that rule with the mobile dock, so on desktop an
 *   always-sticky header never leaves the screen, and on a phone it trades places
 *   with the dock. Scrolling back up restores it immediately.
 *
 * Publishes `hubcart:header:hidden` / `hubcart:header:shown` on `document` so
 * `component-mobile-dock.js` can reveal in step with the header hiding.
 */
(function () {
  'use strict';

  const header = document.getElementById('SiteHeader');
  /* The wrapper is what moves; the header is only how we find it and what we read the
     threshold from. */
  const section = header?.closest('.header-section');
  if (!header || !section) return;

  /**
   * Publishes `--topbar-height` / `--header-height` / `--sticky-header-height` onto the
   * root (`snippets/css-variables.liquid` declares all three at a zero default). Several
   * consumers now size themselves off the real numbers: the collection sidebar and
   * product sticky-form both pin `top` at `--sticky-header-height`, and the product
   * gallery's sticky-panel height formula subtracts `--header-height` and `--topbar-height`
   * from the viewport (`assets/component-product.css`).
   *
   * One `ResizeObserver` covers both elements rather than a `window` resize listener,
   * because a resize listener only catches viewport-width-driven changes — the header's
   * own `padding-block` step at 1024px, mainly — and misses the ones that do not: the
   * announcement bar's height changes with its own content (slideshow text of a
   * different length, a line wrap at a width the observer sees but a resize event does
   * not fire for) and with a webfont arriving after first paint. `ResizeObserver` reports
   * all of those the same way, at the box, without this file needing to know why the box
   * changed. It also fires once synchronously on `observe()`, which is the initial
   * publish — no separate call-on-load is needed.
   */
  /* Declared ahead of `publishHeightVars`, which reads `SCROLLED_CLASS`: a `const` is in
     its temporal dead zone until the line that declares it runs, and the ResizeObserver
     below can dispatch its first callback before the rest of this file has been evaluated. */
  const HIDDEN_CLASS = 'header-section--hidden';
  const SCROLLED_CLASS = 'header-scrolled';

  const topbar = document.querySelector('[data-announcement-bar]');

  function publishHeightVars() {
    const topbarHeight = topbar ? topbar.offsetHeight : 0;
    const headerHeight = header.offsetHeight;

    /* `--sticky-header-height` is measured separately from `--header-height`, not copied
       from it, on purpose: `position: sticky` does not itself change the header's own
       box, so today the two numbers are always equal, but the token exists for a future
       compact-on-scroll state that shrinks the header once it is pinned (a class toggled
       elsewhere on `header` or `section`) — that state would change `header.offsetHeight`
       here too, and this line would already be reading the right thing without edit. */
    const stickyHeaderHeight = header.offsetHeight;

    const root = document.documentElement.style;
    root.setProperty('--topbar-height', topbarHeight + 'px');
    root.setProperty('--header-height', headerHeight + 'px');
    root.setProperty('--sticky-header-height', stickyHeaderHeight + 'px');

    /* How far `#MainContent` is pulled up so a hero sits underneath a transparent header.
       Deliberately NOT `--header-height`: that one tracks the compact state, and a margin
       tied to it would shorten the moment the header pinned — dragging the whole page up
       by the 25% of padding the header had just given back, on the first scroll. Frozen
       while scrolled instead, which costs nothing: the only section this margin positions
       is the first one, and by the time the header compacts it has already left the top of
       the screen. It resumes tracking on the way back up, so the padding step at 768px and
       a late-arriving webfont are both still picked up. */
    if (!section.classList.contains(SCROLLED_CLASS)) {
      root.setProperty('--header-overlay-height', headerHeight + 'px');
    }
  }

  const heightObserver = new ResizeObserver(publishHeightVars);
  /* `border-box`, not the default `content-box`: the compact state changes only the
     header's own padding, which a content-box observation never reports — the numbers
     would go stale exactly when they change. */
  heightObserver.observe(header, { box: 'border-box' });
  if (topbar) heightObserver.observe(topbar, { box: 'border-box' });

  const BUFFER = 10;
  const HIDE_BUFFER = 100;

  /* All thresholds are fixed at load, from the section's document position and the
     header's un-compacted height — the reference does the same (its StickyHeader keeps
     `firstScrollTop`, `initialHeaderHeight` and the section rect from `connectedCallback`).
     Live measurement cannot work here: once the wrapper is pinned its rect top is the
     viewport top, so a threshold derived from it would ride along with the scroll and
     never be crossed; and the compact state shrinks the header, so a live height would
     move the un-compact line and oscillate. The 10px buffer is hysteresis around the
     pin line for the same reason.
     `lastScrollY` starts at -1 so a page restored mid-scroll classifies on the first
     scroll event rather than waiting for the second. */
  const firstScrollTop = window.scrollY;
  const sectionBounds = section.getBoundingClientRect();
  const initialHeaderHeight = header.getBoundingClientRect().height;
  const scrolledThreshold = sectionBounds.top + firstScrollTop + initialHeaderHeight;
  const navThreshold = sectionBounds.top + firstScrollTop + initialHeaderHeight * 2;
  const hideThreshold = sectionBounds.bottom + firstScrollTop + HIDE_BUFFER;

  let lastScrollY = window.scrollY - 1;
  let ticking = false;

  /* ---------------------------------------------------------------------------
     Dropdown navigation (`.header__submenu`, one per menu link that has children).

     The `<details>` element owns the toggle: click, Enter and Space already work, the
     control is a button so opening "Shop" cannot navigate to it by accident, and the menu
     still opens with this file absent. Everything here is the layer on top that a bare
     `<details>` has no opinion about — hover, Escape, closing when focus or the pointer
     leaves, one-open-at-a-time, and the closing animation.

     The closing animation is the only part that needs explaining. An opening panel can
     animate in CSS alone (`@starting-style`), but a closing one cannot: `open` removes the
     panel from rendering immediately, so there is nothing left to transition. So `open`
     is held until the transition ends, with `data-closing` driving the transition and a
     timeout guarding against a `transitionend` that never arrives — a display change, a
     cancelled transition, or a browser that ignored the transition entirely.
  --------------------------------------------------------------------------- */
  const submenus = Array.from(header.querySelectorAll('[data-submenu]'));
  const finePointer = window.matchMedia('(pointer: fine)');

  /* A menu item resolves to one of two panels — the ordinary dropdown or the full-bleed
     department panel — and every routine below cares only that there *is* one to measure,
     hold open and listen to. Which one it got is asked exactly where it matters. */
  const PANEL_SELECTOR = '.header__submenu-panel, .header__mega-inner';

  function panelOf(details) {
    return details.querySelector(PANEL_SELECTOR);
  }
  /* Long enough to outlast `--duration-fast` at the theme's slowest motion scale; it only
     ever runs when `transitionend` has already failed to fire. */
  const CLOSE_FALLBACK_MS = 700;

  /* A close in flight has to be genuinely abortable, not merely flagged as finished. A
     visitor who leaves a menu and comes straight back gets `mouseenter` a few hundred
     milliseconds into the close, and if the pending timer survived that it would shut the
     menu under them a moment later. Each pending close therefore leaves a teardown here
     that removes the listener, clears the timer and drops `data-closing` in one move. */
  const pendingCloses = new WeakMap();

  /* The grace period before a pointer-out is believed.

     A department panel hangs from the *header's* bottom edge (`.header__mega` is
     `inset-block-start: 100%` against `.header`), while the `<details>` that owns it is
     only the menu item itself. Between the item's bottom and the header's is the header's
     own bottom padding — 33px at rest — and that band belongs to neither, so a pointer
     travelling from "Shop" down into the panel it has just opened leaves the `<details>`
     subtree on the way. `mouseleave` fired there, the panel began retracting (measured:
     ~18px of travel at an ordinary pointer speed), and `mouseenter` on the panel then
     cancelled the close — so the menu visibly jerked upward and snapped back every time
     anyone tried to reach it. Pause in the band for longer than the close animation and
     it shut outright, leaving the visitor pointing at nothing.

     Deferring the close by a little longer than that transit takes fixes it without any
     geometry: the pointer is back inside before the timer fires, and `mouseenter` clears
     it. A genuine leave is delayed by the same amount, which is below the threshold at
     which a menu feels sticky. The ordinary dropdown gets the same treatment — it hangs
     from the item rather than the header, so it has no band today, but nothing about the
     fix depends on which panel it is. */
  const GRACE_MS = 160;
  const graceTimers = new WeakMap();

  function clearGrace(details) {
    const timer = graceTimers.get(details);
    if (timer === undefined) return;
    clearTimeout(timer);
    graceTimers.delete(details);
  }

  function cancelClose(details) {
    clearGrace(details);
    const teardown = pendingCloses.get(details);
    if (teardown) teardown();
  }

  function closeSubmenu(details) {
    /* Escape, an outside click and "close the others" all arrive here directly and must
       not be undone a moment later by a grace timer still counting down from a pointer-out
       that happened first. */
    clearGrace(details);
    if (!details.open || pendingCloses.has(details)) return;

    /* `offsetParent` is null when the panel is not being rendered — below 768px the whole
       desktop nav is `display: none`, and a display-none element never fires
       `transitionend`. Closing it outright is both correct and instant; leaving it to the
       fallback timer would hold `open` for most of a second for no visible reason. */
    const panel = panelOf(details);
    if (!panel || panel.offsetParent === null) {
      details.open = false;
      return;
    }

    let done = false;
    const teardown = () => {
      done = true;
      panel.removeEventListener('transitionend', onEnd);
      clearTimeout(timer);
      pendingCloses.delete(details);
      delete details.dataset.closing;
    };
    const finish = () => {
      if (done) return;
      teardown();
      details.open = false;
    };
    /* Both `opacity` and `transform` transition; either one arriving means the panel has
       finished moving, and `finish` runs at most once. */
    const onEnd = (event) => {
      if (event.target === panel) finish();
    };

    panel.addEventListener('transitionend', onEnd);
    /* The guard against a `transitionend` that never arrives — a display change, a
       cancelled transition, or a browser that ignored the transition entirely. */
    const timer = setTimeout(finish, CLOSE_FALLBACK_MS);
    pendingCloses.set(details, teardown);
    details.dataset.closing = 'true';
  }

  /* The panel hangs from its item's leading edge, which is right for every item except one
     close enough to the trailing edge that a 15rem panel would run off the viewport and
     give the page a horizontal scrollbar. There is no CSS-only test for that — it depends
     on where the item lands, which depends on how many items the merchant's menu has — so
     it is measured on open and the anchor flipped to the trailing edge when it fails.

     Measured every time rather than once, because the item moves: the header pins and
     re-pads at `navThreshold`, and the menu itself can change under the theme editor. */
  function alignSubmenu(details) {
    /* A department panel spans the header, so there is no edge for it to run off and
       nothing to flip. Only the item-anchored dropdown needs measuring. */
    const panel = details.querySelector('.header__submenu-panel');
    if (!panel) return;

    delete details.dataset.align;
    const room = document.documentElement.clientWidth - details.getBoundingClientRect().left;
    if (panel.getBoundingClientRect().width > room) details.dataset.align = 'end';
  }

  function closeOtherSubmenus(except) {
    submenus.forEach((details) => {
      if (details !== except) closeSubmenu(details);
    });
  }

  /* ---------------------------------------------------------------------------
     Department tabs, inside a mega panel only.

     Pointing at a department swaps the products beside it; clicking it goes to that
     department. Hover rather than click is what selects, because the click is already
     spoken for — and because a pointer travelling down the list previews four
     departments in the time a click-to-select would show one. Focus does the same thing
     for the keyboard, so tabbing through the list reveals each department in turn without
     needing a second key, and the arrow keys move between them for anyone who expects a
     list to behave like one.

     Only `data-active` moves; the hiding itself is CSS, and `visibility: hidden` is what
     keeps an inactive department's links out of the tab order and the accessibility tree
     (see the stylesheet for why it is that rather than `hidden` or `display: none`).
  --------------------------------------------------------------------------- */
  function setupMegaTabs(details) {
    const tabs = Array.from(details.querySelectorAll('[data-mega-tab]'));
    const panels = Array.from(details.querySelectorAll('[data-mega-panel]'));
    if (tabs.length < 2 || !panels.length) return null;

    function select(index) {
      tabs.forEach((tab, i) => {
        if (i === index) tab.dataset.active = 'true';
        else delete tab.dataset.active;
      });
      panels.forEach((panel, i) => {
        if (i === index) panel.dataset.active = 'true';
        else delete panel.dataset.active;
      });
    }

    tabs.forEach((tab, index) => {
      /* `pointerenter`, not `mouseenter`: this is the one listener a visitor can reach
         with a stylus or a trackpad-driven cursor as well as a mouse, and it does not
         bubble, so a nested icon cannot re-fire it. */
      tab.addEventListener('pointerenter', () => select(index));
      tab.addEventListener('focus', () => select(index));

      tab.addEventListener('keydown', (event) => {
        let next = null;
        if (event.key === 'ArrowDown') next = (index + 1) % tabs.length;
        else if (event.key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        /* Otherwise the page scrolls under the open panel while focus moves inside it. */
        event.preventDefault();
        tabs[next].focus();
      });
    });

    return () => select(0);
  }

  submenus.forEach((details) => {
    const summary = details.querySelector('summary');
    if (!summary) return;

    const resetMegaTabs = setupMegaTabs(details);

    /* `toggle` rather than the summary's click: it is the one event that fires for every
       route into the open state — pointer, keyboard, and this file's own hover — so
       `aria-expanded` cannot drift from what is on screen. */
    details.addEventListener('toggle', () => {
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      /* Back to the first department every time it shuts, so the panel always opens on
         the same thing rather than on whatever the last visit happened to leave selected. */
      if (!details.open) {
        resetMegaTabs?.();
        return;
      }
      closeOtherSubmenus(details);
      alignSubmenu(details);
    });

    /* `open` is still true while the panel animates out, so the native toggle would read a
       click on the summary as "close" — correct — but only after the pending close had
       already done it, and the two racing produces a flicker. Finishing the close here and
       suppressing the toggle keeps the control's meaning ("this closes it") intact. */
    summary.addEventListener('click', (event) => {
      if (pendingCloses.has(details)) {
        event.preventDefault();
        cancelClose(details);
        details.open = false;
        return;
      }

      /* `detail > 0` is what separates a real pointer click from the synthetic one
         `<summary>` dispatches for Enter and Space. Everything below is therefore about
         the pointer only, and the keyboard keeps the native toggle — which for a
         department is the sole way into the panel, since there is no hovering with a
         keyboard. */
      if (!finePointer.matches || event.detail === 0) return;

      /* A top-level department does two jobs at once: hover opens its panel, a click goes
         to the department. `data-link` is the merchant's own URL for that menu item, read
         off the menu in Liquid — the theme never invents a destination, and an item
         without one falls through to the behaviour below. */
      const destination = summary.dataset.link;
      if (destination) {
        event.preventDefault();
        window.location.href = destination;
        return;
      }

      /* Where the pointer can hover, hover already owns the open state: by the time a
         click lands on the summary the panel is open because the pointer is on it. The
         native toggle would read that click as "close" and shut a menu the visitor is
         still pointing at, leaving them on a closed control that only reopens by moving
         away and back. Hover-out, Escape and outside-click remain the ways to close. */
      if (details.open) event.preventDefault();
    });

    /* Hover is an enhancement for pointers that can hover, not the mechanism. A touch
       device gets the tap-to-open `<details>` behaviour untouched, which is why this is
       gated rather than paired with a `touchstart` workaround. */
    details.addEventListener('mouseenter', () => {
      if (!finePointer.matches) return;
      cancelClose(details);
      details.open = true;
    });

    details.addEventListener('mouseleave', () => {
      if (!finePointer.matches) return;
      /* Not `closeSubmenu` directly — see `GRACE_MS`. The pointer has to still be gone
         when the timer fires for the leave to count. */
      clearGrace(details);
      graceTimers.set(
        details,
        setTimeout(() => {
          graceTimers.delete(details);
          closeSubmenu(details);
        }, GRACE_MS)
      );
    });

    /* Tabbing out of the last link in a panel should close it — otherwise a keyboard
       visitor leaves an open menu behind them for the rest of the page. Deferred by a
       frame because `focusout` fires before the incoming element has focus, so
       `activeElement` is still `<body>` at this point. */
    details.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (!details.contains(document.activeElement)) closeSubmenu(details);
      });
    });
  });

  if (submenus.length) {
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const open = submenus.find((details) => details.open && !pendingCloses.has(details));
      if (!open) return;
      /* Focus goes back to the control that opened the panel, not wherever it happened to
         be inside it — otherwise Escape leaves focus on an element that no longer renders
         and the next Tab restarts from the top of the document. */
      closeSubmenu(open);
      open.querySelector('summary')?.focus();
    });

    /* `pointerdown`, not `click`: a click on a link inside the panel would otherwise close
       the panel and, in some browsers, retarget the click. Anything outside the header
       closes. */
    document.addEventListener('pointerdown', (event) => {
      if (header.contains(event.target)) return;
      closeOtherSubmenus(null);
    });
  }

  /* The drawer's levels, which are the same navigation at a width where a floating panel
     has nowhere to go. Not the behaviour above: these are drilled into rather than hovered,
     they never close one another (a level stays open behind the one in front of it), and
     the drawer's own Escape closes the drawer rather than a level. Queried from the
     document because the drawer is a sibling of the `<header>`, not a descendant of it.

     `<details>` stays the element and this only decorates it. Marking the root is what
     switches the CSS from accordion to panel, so if this block never runs — a parse error
     earlier in the file, a blocked asset — the menu is still a working accordion instead of
     a set of panels parked off-screen. */
  setupMenuDrawer(document.getElementById('MenuDrawer'));

  function setupMenuDrawer(drawer) {
    const root = drawer?.querySelector('[data-menu-root]');
    if (!root) return;

    const groups = Array.from(root.querySelectorAll('[data-menu-group]'));
    if (!groups.length) return;

    root.setAttribute('data-menu-enhanced', '');

    /* A level closing is deferred until its panel has finished travelling, so `open` cannot
       be trusted to say whether a level is on screen. `is-active` is the truth the CSS and
       this map both read; `open` only governs whether the panel is rendered at all. */
    const pendingHides = new Map();

    function cancelHide(group) {
      const pending = pendingHides.get(group);
      if (!pending) return;
      clearTimeout(pending.timer);
      group.removeEventListener('transitionend', pending.onEnd);
      pendingHides.delete(group);
    }

    /* The siblings a level leaves behind stay in the DOM, one opaque panel underneath the
       one in front. Without this they stay tabbable and readable, so a screen reader walks
       straight off the visible panel into a list nobody can see. */
    function setSiblingsInert(group, inert) {
      const item = group.parentElement;
      const parent = group.closest('[data-menu-parent]');
      if (!item || !parent) return;
      Array.from(parent.children).forEach((sibling) => {
        if (sibling === item) return;
        sibling.inert = inert;
      });
    }

    function show(group) {
      const summary = group.querySelector(':scope > summary');
      const panel = group.querySelector(':scope > [data-menu-panel]');
      const back = panel?.querySelector(':scope > [data-menu-back]');
      if (!summary || !panel) return;

      cancelHide(group);
      group.open = true;
      summary.setAttribute('aria-expanded', 'true');
      /* The summary is behind the panel now. Leaving it in the tab order is the same bug as
         the siblings above, but it cannot be `inert` — it is the panel's own ancestor. */
      summary.setAttribute('tabindex', '-1');
      setSiblingsInert(group, true);
      group.closest('[data-menu-parent]')?.classList.add('is-shifted');

      /* One frame between rendering the panel and animating it. `group.open = true` is what
         makes the panel exist; a class added in the same frame would be part of its first
         style resolution and the transition would never start. */
      requestAnimationFrame(() => {
        group.classList.add('is-active');
        /* Focus follows the panel, and lands on the way out of it — the first thing in the
           panel and the one control that undoes the action just taken. */
        back?.focus({ preventScroll: true });
      });
    }

    function hide(group, returnFocus) {
      const summary = group.querySelector(':scope > summary');
      const panel = group.querySelector(':scope > [data-menu-panel]');
      if (!summary || !panel) return;

      cancelHide(group);
      group.classList.remove('is-active');
      summary.setAttribute('aria-expanded', 'false');
      summary.removeAttribute('tabindex');
      setSiblingsInert(group, false);
      group.closest('[data-menu-parent]')?.classList.remove('is-shifted');
      if (returnFocus) summary.focus({ preventScroll: true });

      /* `open` comes off only once the panel has left, or the panel would vanish mid-slide.
         The timeout is the guard for the transition that never reports — a panel scrolled
         out of view, or a browser that drops the event under load. */
      const finish = () => {
        cancelHide(group);
        group.open = false;
      };
      const onEnd = (event) => {
        if (event.target !== panel || event.propertyName !== 'transform') return;
        finish();
      };
      group.addEventListener('transitionend', onEnd);
      pendingHides.set(group, { onEnd, timer: setTimeout(finish, 700) });
    }

    groups.forEach((group) => {
      const summary = group.querySelector(':scope > summary');
      if (!summary) return;

      /* The native toggle is suppressed on every route in — pointer, Enter and Space all
         arrive here — because opening has to be sequenced against a frame boundary and the
         native one opens immediately. `show()` sets `open` itself. */
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (group.classList.contains('is-active')) hide(group, true);
        else show(group);
      });

      group
        .querySelector(':scope > [data-menu-panel] > [data-menu-back]')
        ?.addEventListener('click', () => hide(group, true));
    });

    /* Reopening the drawer must land on the top level. Without this the levels the visitor
       drilled into last time are still open behind the closed drawer, and the menu reappears
       three panels deep. Reset on close rather than on open, so it happens while nothing is
       on screen and no panel is seen sliding back. */
    drawer.addEventListener('drawer:close', () => {
      groups.forEach((group) => {
        cancelHide(group);
        group.classList.remove('is-active');
        group.open = false;
        setSiblingsInert(group, false);
        const summary = group.querySelector(':scope > summary');
        summary?.setAttribute('aria-expanded', 'false');
        summary?.removeAttribute('tabindex');
      });
      root
        .querySelectorAll('.is-shifted')
        .forEach((parent) => parent.classList.remove('is-shifted'));
    });
  }

  /* Past twice the header height, an open dropdown would float with nothing visibly
     anchoring it; the reference closes them there. */
  function closeOpenDetails() {
    submenus.forEach(closeSubmenu);
  }

  function update() {
    ticking = false;

    const scrollY = window.scrollY;
    const scrollingDown = scrollY > lastScrollY;
    const isHidden = section.classList.contains(HIDDEN_CLASS);
    const isScrolled = section.classList.contains(SCROLLED_CLASS);

    /* Compact-on-scroll, with 10px of hysteresis on each side of the pin line so the
       padding animation cannot flutter when the page rests exactly on the threshold. */
    if (!isScrolled && scrollY > scrolledThreshold + BUFFER) {
      section.classList.add(SCROLLED_CLASS);
    } else if (isScrolled && scrollY < scrolledThreshold - BUFFER) {
      section.classList.remove(SCROLLED_CLASS);
    }

    if (scrollY > navThreshold) closeOpenDetails();

    /* Hidden state: past the section's bottom + 100px AND scrolling down; any upward
       scroll, or scrolling back above the threshold, restores it. Desktop CSS ignores
       the class (the reference's always-sticky header never hides there); on phones it
       slides the header up and the mobile dock in. */
    if (scrollY > hideThreshold && scrollingDown) {
      if (!isHidden) {
        section.classList.add(HIDDEN_CLASS);
        closeOpenDetails();
        document.dispatchEvent(new CustomEvent('hubcart:header:hidden'));
      }
    } else if (isHidden) {
      section.classList.remove(HIDDEN_CLASS);
      document.dispatchEvent(new CustomEvent('hubcart:header:shown'));
    }

    lastScrollY = scrollY;
  }

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    },
    { passive: true }
  );
})();